import React, { useEffect, useState } from 'react';
import axios from 'axios';
import stylesDefault from '../default.module.css';
import stylesSpec from './contactForm.module.css';
import { showErrorToast } from '../../../../code/ToastHelper';

export default function ContactForm({ contact, onSave, onClose }) {
    const [formData, setFormData] = useState({
        first_name: '',
        family_name: '',
        tel: '',
        email: '',
        img: '',
    });
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState('/uploads/personen/default_person.svg');
    const [imageFile, setImageFile] = useState(null);
    const [prevImage, setPrevImage] = useState('');

    useEffect(() => {
        if (contact && contact.id) {
            const existingImg = contact.img || '';
            setFormData({
                first_name: contact.first_name || '',
                family_name: contact.family_name || '',
                tel: contact.tel || '',
                email: contact.email || '',
                img: existingImg,
            });
            setImagePreview(existingImg ? `/uploads/personen/${existingImg}` : '/uploads/personen/default_person.svg');
            setPrevImage(existingImg); // Track it for possible deletion
        } else if (contact) {
            setFormData({ first_name: '', family_name: '', tel: '', email: '', img: '' });
            setImagePreview('/uploads/personen/default_person.svg');
            setPrevImage('');
        }
        setImageFile(null);
    }, [contact]);

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
            // Clear img string to indicate new upload
            setFormData(prev => ({ ...prev, img: '' }));
        }
    };

    const handleDeleteImage = async () => {
        // Don't delete if it's already the default image
        if (formData.img && formData.img !== 'default_person.svg') {
            try {
                await axios.delete(`/api/upload/delete-person-image/${formData.img}`);
            } catch (err) {
                console.error('Fehler beim Löschen des Bildes:', err);
            }
        }

        setImageFile(null);
        setFormData(prev => ({ ...prev, img: '' }));
        setPrevImage(''); // Clear prev image tracking
        setImagePreview('/uploads/personen/default_person.svg');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            let imgFilename = formData.img;

            // If a new file was selected, upload it
            if (imageFile) {
                const uploadData = new FormData();
                uploadData.append('first_name', formData.first_name);
                uploadData.append('family_name', formData.family_name);
                uploadData.append('image', imageFile);


                const uploadRes = await axios.post('/api/upload/upload-person-image', uploadData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                imgFilename = uploadRes.data.filename;

                // Delete old image if there was one and it's not the default
                if (prevImage && prevImage !== 'default_person.svg') {
                    await axios.delete(`/api/upload/delete-person-image/${prevImage}`);
                }
            }

            const payload = { ...formData, img: imgFilename };

            if (contact.id) {
                await axios.put(`/api/contact/${contact.id}`, payload);
                onSave(contact.id, 'update');
            } else {
                const res = await axios.post('/api/contact/', payload);
                onSave(res.data.id, 'create');
            }
        } catch (err) {
            console.error('Fehler beim Speichern:', err);
            showErrorToast('Fehler beim Speichern des Kontakts.');
        }

        setLoading(false);
    };

    return (
        <div className={stylesSpec.formOverlay}>
            <form className={stylesSpec.contactForm} onSubmit={handleSubmit}>
                <h2>{contact.id ? 'Kontakt bearbeiten' : 'Neuen Kontakt anlegen'}</h2>

                {/* Image preview and controls */}
                <div className={stylesSpec.imageSection}>
                    <button
                        type="button"
                        onClick={onClose}
                        className={stylesSpec.closeButton}
                        aria-label="Modal schließen"
                    >
                        ×
                    </button>
                    <img
                        src={imagePreview}
                        alt="Profilbild"
                        className={stylesSpec.profileImage}
                    />
                    <div className={stylesSpec.imageButtons}>
                        <input
                            type="file"
                            accept="image/*"
                            id="imageUpload"
                            style={{ display: 'none' }}
                            onChange={handleImageChange}
                        />
                        <label htmlFor="imageUpload" className={stylesDefault.blueButton}>
                            Bild hochladen
                        </label>

                        <button
                            type="button"
                            className={`${stylesDefault.redButton}`}
                            onClick={handleDeleteImage}
                            disabled={imagePreview.endsWith('default_person.svg')}
                        >
                            Bild löschen
                        </button>
                    </div>
                </div>

                {/* Meta data fields */}
                <label>
                    Vorname:
                    <input
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                    />
                </label>

                <label>
                    Nachname:
                    <input
                        name="family_name"
                        value={formData.family_name}
                        onChange={handleChange}
                        required
                    />
                </label>

                <label>
                    Telefon:
                    <input
                        name="tel"
                        value={formData.tel}
                        onChange={handleChange}
                    />
                </label>

                <label>
                    E-Mail:
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                    />
                </label>

                <div className={stylesDefault.formButtons}>
                    <button className={stylesDefault.blueButton} type="submit" disabled={loading}>
                        {loading ? 'Speichert...' : (contact.id ? 'Aktualisieren' : 'Erstellen')}
                    </button>
                    <button className={stylesDefault.redButton} type="button" onClick={onClose} disabled={loading}>
                        Abbrechen
                    </button>
                </div>
            </form>
        </div>
    );
}
